import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Switch,
  FormControlLabel,
  Typography,
  Divider,
  Grid,
} from "@mui/material";
import { useQuery, useMutation } from "@apollo/client";
import { GET_ME, UPDATE_USER } from "../graphql/users";
import { User } from "../types/user";

interface UserEditDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved?: (user: User) => void;
}

export const UserEditDialog: React.FC<UserEditDialogProps> = ({
  open,
  onClose,
  onSaved,
}) => {
  const { data, loading } = useQuery(GET_ME, { skip: !open });
  const [updateUser, { loading: saving }] = useMutation(UPDATE_USER);
  const [form, setForm] = useState<
    Partial<User & { emailVerified: boolean; notificationPreferences: string }>
  >({
    defaultTradeFee: 0,
    defaultPositionFee: 0,
    feeCurrency: "USD",
  });

  useEffect(() => {
    if (data?.me) {
      setForm({
        ...data.me,
        emailVerified: data.me.emailVerified ?? false,
        notificationPreferences:
          typeof data.me.notificationPreferences === "string"
            ? data.me.notificationPreferences
            : JSON.stringify(data.me.notificationPreferences ?? {}, null, 2),
        defaultTradeFee: data.me.defaultTradeFee ?? 0,
        defaultPositionFee: data.me.defaultPositionFee ?? 0,
        feeCurrency: data.me.feeCurrency ?? "USD",
      });
    }
  }, [data, open]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value, type } = e.target;
    let newValue: any = value;

    // Handle checkbox inputs
    if (type === "checkbox" && "checked" in e.target) {
      newValue = (e.target as HTMLInputElement).checked;
    }

    // Handle numeric inputs for fee fields
    if (
      type === "number" &&
      (name === "defaultTradeFee" || name === "defaultPositionFee")
    ) {
      newValue = value === "" ? undefined : Number(value);
    }

    setForm((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleSave = async () => {
    if (!data?.me) return;
    await updateUser({
      variables: {
        id: data.me.id,
        data: {
          email: form.email,
          emailVerified: form.emailVerified,
          pushbulletKey: form.pushbulletKey,
          phoneNumber: form.phoneNumber,
          telegramId: form.telegramId,
          notificationPreferences: form.notificationPreferences
            ? JSON.parse(form.notificationPreferences)
            : {},
          defaultTradeFee: form.defaultTradeFee,
          defaultPositionFee: form.defaultPositionFee,
          feeCurrency: form.feeCurrency,
        },
      },
    });
    if (onSaved) onSaved({ ...data.me, ...form } as User);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit User</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <TextField
            label="Email"
            name="email"
            value={form.email || ""}
            onChange={handleChange}
            fullWidth
            disabled={loading}
          />
          <FormControlLabel
            control={
              <Switch
                checked={!!form.emailVerified}
                onChange={handleChange}
                name="emailVerified"
                color="primary"
                disabled={loading}
              />
            }
            label="Email Verified"
          />
          <TextField
            label="Pushbullet Key"
            name="pushbulletKey"
            value={form.pushbulletKey || ""}
            onChange={handleChange}
            fullWidth
            disabled={loading}
          />
          <TextField
            label="Phone Number"
            name="phoneNumber"
            value={form.phoneNumber || ""}
            onChange={handleChange}
            fullWidth
            disabled={loading}
          />
          <TextField
            label="Telegram ID"
            name="telegramId"
            value={form.telegramId || ""}
            onChange={handleChange}
            fullWidth
            disabled={loading}
          />
          <TextField
            label="Notification Preferences (JSON)"
            name="notificationPreferences"
            value={form.notificationPreferences || ""}
            onChange={handleChange}
            fullWidth
            multiline
            minRows={2}
            disabled={loading}
          />

          <Divider />

          <Typography variant="h6" gutterBottom>
            Fee Settings
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="Default Trade Fee (%)"
                name="defaultTradeFee"
                type="number"
                value={form.defaultTradeFee ?? ""}
                onChange={handleChange}
                fullWidth
                disabled={loading}
                inputProps={{
                  step: 0.01,
                  min: 0,
                  max: 100,
                }}
                helperText="Trade fee %"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Default Position Fee (%)"
                name="defaultPositionFee"
                type="number"
                value={form.defaultPositionFee ?? ""}
                onChange={handleChange}
                fullWidth
                disabled={loading}
                inputProps={{
                  step: 0.01,
                  min: 0,
                  max: 100,
                }}
                helperText="Position fee %"
              />
            </Grid>
          </Grid>

          <TextField
            label="Fee Currency"
            name="feeCurrency"
            value={form.feeCurrency ?? "USD"}
            onChange={handleChange}
            fullWidth
            disabled={loading}
            helperText="Currency for fee calculations"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || loading}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
